import { useMemo, useState } from "react";
import { filterSessionHistory } from "./activityScope";
import { formatDateLong, formatDuration, formatTime, statusMeta } from "./formatters";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "live", label: "Live" },
  { id: "completed", label: "Completed" },
];

const SORT_OPTIONS = [
  { id: "newest", label: "Newest first" },
  { id: "oldest", label: "Oldest first" },
];

export default function SessionHistoryTable({
  sessions = [],
  onView,
  onSelectUser,
  showUserColumn = true,
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const rows = useMemo(
    () => filterSessionHistory(sessions, { status: statusFilter, sort }),
    [sessions, sort, statusFilter],
  );
  const countLabel = `${rows.length} ${rows.length === 1 ? "session" : "sessions"}`;

  return (
    <>
      <div className="pa-section__head">
        <div>
          <h2 className="pa-section__title">
            Session history <span className="pa-count">{countLabel}</span>
          </h2>
        </div>
        <div className="pa-history-tools">
          <div className="pa-preset-group" role="group" aria-label="Session status">
            {STATUS_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`pa-pill${statusFilter === item.id ? " pa-pill--active" : ""}`}
                onClick={() => setStatusFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="pa-preset-group" role="group" aria-label="Session sort">
            {SORT_OPTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`pa-pill${sort === item.id ? " pa-pill--active" : ""}`}
                onClick={() => setSort(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!sessions.length ? (
        <p className="pa-empty">No session history for this period.</p>
      ) : !rows.length ? (
        <p className="pa-empty">
          {statusFilter === "live"
            ? "No live sessions in this period."
            : "No completed sessions in this period."}
        </p>
      ) : (
        <div className="pa-history-lock app-table-scroll scrollable">
          <table className="pa-table">
            <thead>
              <tr>
                {showUserColumn ? <th>User</th> : null}
                <th>Start date</th>
                <th>Start</th>
                <th className="pa-col-end">End date</th>
                <th className="pa-col-end">End</th>
                <th>Active time</th>
                <th>Paused</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((session) => {
                const status = statusMeta(session.status);
                const label = session.userName || session.userId || "—";
                return (
                  <tr key={session.sessionId}>
                    {showUserColumn ? (
                      <td>
                        {session.userId && onSelectUser ? (
                          <button
                            type="button"
                            className="pa-user-link"
                            onClick={() => onSelectUser(session)}
                          >
                            {label}
                          </button>
                        ) : (
                          label
                        )}
                      </td>
                    ) : null}
                    <td>{formatDateLong(session.startTime)}</td>
                    <td>{formatTime(session.startTime)}</td>
                    <td className="pa-col-end">{formatDateLong(session.endTime)}</td>
                    <td className="pa-col-end">{formatTime(session.endTime)}</td>
                    <td className="pa-active">{formatDuration(session.activeSeconds)}</td>
                    <td>{formatDuration(session.pausedSeconds)}</td>
                    <td>
                      <span className={`pa-badge pa-badge--${status.tone}`}>{status.label}</span>
                    </td>
                    <td>
                      <button type="button" className="pa-view" onClick={() => onView(session)}>
                        View →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
